import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const MonthlyAnnualBalanceSheet = ({ annualSummary, monthlySchedule, totalMetrics }) => {
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  const parseDate = (dateString) => {
    const date = new Date(dateString);
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      formattedDate: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
    };
  };

  const years = useMemo(() => {
    if (!annualSummary || !Array.isArray(annualSummary) || annualSummary.length === 0) {
      return [];
    }
    return [...new Set(annualSummary.map(item => parseDate(item.index).year))];
  }, [annualSummary]);

  const formatCurrency = (value) => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatPercentage = (value) => {
    if (value == null) return '-';
    return (value * 100).toFixed(2) + '%';
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const toggleRow = (rowKey) => {
    setExpandedRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const renderTableHeader = () => {
    return (
      <tr className="ace-table-header custom-text-size-header">
        <th className="px-4 py-2 text-left" style={{ width: "calc(12rem * 1.3)" }}>Metric</th>
        {years.map(year => (
          <React.Fragment key={year}>
            {expandedYears[year] && Array.from({length: 12}, (_, i) => (
              <th key={`${year}-${i+1}`} className="px-2 py-2 text-right">
                {format(new Date(year, i, 1), 'MMM')} {year}
              </th>
            ))}
            <th 
              className="px-2 py-2 text-right cursor-pointer" 
              onClick={() => toggleYear(year)}
            >
              {year} {expandedYears[year] ? <ChevronDown className="inline" /> : <ChevronRight className="inline" />}
            </th>
          </React.Fragment>
        ))}
        <th className="px-2 py-2 text-right">Total/Avg</th>
      </tr>
    );
  };

  const renderDataRow = (metric, label, isSubItem = false, isTotalRow = false, formatFunc = formatCurrency, hasSubItems = false) => {
    const isExpanded = expandedRows[metric];
    const cumulativeMetric = `${metric}_cumulative`;

    return (
      <tr className={`custom-text-size-body ${isTotalRow ? 'font-bold' : ''} hover:bg-muted/50`}>
        <td 
          className={`px-4 py-2 ${isSubItem ? 'pl-8' : ''}`} 
          style={{ width: "calc(12rem * 1.3)", cursor: hasSubItems ? 'pointer' : 'default' }}
          onClick={() => hasSubItems && toggleRow(metric)}
        >
          <span className="flex items-center justify-between">
            {label}
            {hasSubItems && (
              <span className="ml-2">
                {isExpanded ? (
                  <ChevronDown className="inline" style={{ width: '0.84em', height: '0.84em' }} />
                ) : (
                  <ChevronRight className="inline" style={{ width: '0.84em', height: '0.84em' }} />
                )}
              </span>
            )}
          </span>
        </td>
        {years.map(year => (
          <React.Fragment key={year}>
            {expandedYears[year] && Array.from({length: 12}, (_, i) => {
              const monthData = monthlySchedule?.find(item => {
                const itemDate = parseDate(item.index);
                return itemDate.year === year && itemDate.month === i;
              });
              const value = monthData ? monthData[metric] : null;
              const isNegative = typeof value === 'number' && value < 0;
              return (
                <td key={`${year}-${i+1}`} className={`px-2 py-2 text-right ${isNegative ? 'text-destructive' : ''}`}>
                  {value !== null ? formatFunc(value) : '-'}
                </td>
              );
            })}
            {(() => {
              const annualVal = annualSummary?.find(item => parseDate(item.index).year === year)?.[metric];
              const annualCum = annualSummary?.find(item => parseDate(item.index).year === year)?.[cumulativeMetric];
              const annualNeg = typeof annualVal === 'number' && annualVal < 0;
              return (
                <td className={`px-2 py-2 text-right font-bold ${annualNeg ? 'text-destructive' : ''}`}>
                  <div>{formatFunc(annualVal)}</div>
                  {annualCum != null && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Cum: {formatFunc(annualCum)}
                    </div>
                  )}
                </td>
              );
            })()}
          </React.Fragment>
        ))}
        {(() => {
          const totalVal = totalMetrics?.[`total_${metric}`] || totalMetrics?.[`average_${metric}`] || totalMetrics?.[metric] || null;
          const totalNeg = typeof totalVal === 'number' && totalVal < 0;
          const totalCum = totalMetrics?.[`total_${cumulativeMetric}`] || totalMetrics?.[`average_${cumulativeMetric}`] || totalMetrics?.[cumulativeMetric] || null;
          return (
            <td className={`px-2 py-2 text-right font-bold ${totalNeg ? 'text-destructive' : ''}`}>
              <div>{formatFunc(totalVal)}</div>
              {totalCum != null && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Cum: {formatFunc(totalCum)}
                </div>
              )}
            </td>
          );
        })()}
      </tr>
    );
  };

  return (
    <Card className="card-wrapper mt-6">
      <CardHeader>
        <CardTitle className="custom-text-size">Balance Sheet</CardTitle>
      </CardHeader>
      <CardContent>
        <style jsx>{`
          .custom-text-size {
            font-size: 110%;
          }
          .custom-text-size-body {
            font-size: calc(0.75rem * 1.1);
          }
          .custom-text-size-header {
            font-size: 95%;
          }
        `}</style>
        <div className="ace-scroll-shell overflow-x-auto">
          <table className="min-w-full">
            <thead className="custom-text-size-header">
              {renderTableHeader()}
            </thead>
            <tbody className="custom-text-size-body">
              {renderDataRow('total_assets', 'Total Assets', false, true, formatCurrency, true)}
              {expandedRows['total_assets'] && (
                <>
                  {renderDataRow('loan_balance', 'Loan Balance', true, false, formatCurrency)}
                  {renderDataRow('line_balance', 'Line Balance', true, false, formatCurrency)}
                </>
              )}
              {renderDataRow('fas91_balance', 'FAS91 Balance')}
              {renderDataRow('total_liabilities', 'Total Liabilities', false, true, formatCurrency, true)}
              {expandedRows['total_liabilities'] && (
                <>
                  {renderDataRow('deposit_balance', 'Deposit Balance', true, false, formatCurrency)}
                </>
              )}
              {renderDataRow('total_equity', 'Total Equity', false, true)}
              {renderDataRow('loan_balance_cagr', 'Loan Balance CAGR', false, false, formatPercentage)}
              {renderDataRow('line_balance_cagr', 'Line Balance CAGR', false, false, formatPercentage)}
              {renderDataRow('deposit_balance_cagr', 'Deposit Balance CAGR', false, false, formatPercentage)}
              {renderDataRow('total_assets_cagr', 'Total Assets CAGR', false, false, formatPercentage)}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyAnnualBalanceSheet;